/* eslint-disable @typescript-eslint/no-explicit-any */
import { afterNextRender, Component, computed, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, of, Subject, switchMap, tap } from 'rxjs';
import { IBibleBookModel, IBibleVerseModel } from '../../../../models/bible.model';
import { BibleService } from '../../../../services/api/bible/bible.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { IBasePopupModel } from '../../models/popup.model';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-bible-verse-popup',
    standalone: false,
    templateUrl: './bible-verse-popup.component.html',
    styleUrl: './bible-verse-popup.component.scss',
})
export class BibleVersePopupComponent extends BasePopupComponent {
    protected bibleBooks = signal<IBibleBookModel[]>([]);
    protected oldTestamentBooks = computed(() => this.bibleBooks().filter((b) => b.category === 'old'));
    protected newTestamentBooks = computed(() => this.bibleBooks().filter((b) => b.category === 'new'));
    protected bibleVerses = signal<IBibleVerseModel[]>([]);
    protected selectedVerseIds = signal<number[]>([]);
    protected selectedVerses = computed(() => {
        const selectedIds = this.selectedVerseIds();

        if (this.selectedTab !== 'Search') {
            return this.bibleVerses().filter((v) => selectedIds.includes(v.id));
        }

        return this.showSearchVersesResult().filter((v) => selectedIds.includes(v.id));
    });
    protected showSearchVersesResult = signal<IBibleVerseModel[]>([]);
    protected selectedSearchVerseId = signal<number | null>(null);
    protected selectedBibleBookId = signal<number | null>(null);
    protected selectedChapterId = signal<number | null>(null);
    protected selectedTab: string = 'Browse';
    protected filteredChapters = computed(() => {
        const selectedId = this.selectedBibleBookId();
        if (!selectedId) {
            return [];
        }
        const book = this.bibleBooks().find((b) => b.id === selectedId);
        return book?.chapters || [];
    });

    private searchSubject = new Subject<string>();

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: any,
        protected override dialogRef: MatDialogRef<BibleVersePopupComponent>,
        private bibleService: BibleService,
        private dialog: MatDialog,
        private loaderService: LoaderService,
    ) {
        super(data, dialogRef);
        afterNextRender(() => {
            this.loadBibleBooks();
            this.setupSearchListener();
        });
    }

    static override getDialogConfig(): MatDialogConfig<IBasePopupModel> {
        return {
            maxWidth: '768px',
        };
    }

    getSelectedVerseReference(): string {
        const verses = this.selectedVerses();
        if (verses.length === 0) {
            return '';
        }

        const first = verses[0];
        const last = verses[verses.length - 1];

        const bookType = first.type;
        const chapter = first.chapter;

        const versePart = verses.length === 1 ? `${first.list}` : `${first.list}-${last.list}`;

        return `${bookType} ${chapter}:${versePart}`;
    }

    protected onRemoveVerse(): void {
        this.selectedVerseIds.set([]);
    }

    protected onTabSelect(tab: string): void {
        if (this.selectedTab !== tab) {
            this.showSearchVersesResult.set([]);
            this.selectedSearchVerseId.set(null);
            this.selectedVerseIds.set([]);
            this.selectedChapterId.set(null);
            this.selectedBibleBookId.set(null);
            this.selectedTab = tab;
        }
    }

    protected selectBibleBook(bookId: number): void {
        this.selectedBibleBookId.set(bookId);
        this.selectedChapterId.set(null);
        this.selectedVerseIds.set([]);
        this.bibleVerses.set([]);
    }

    protected toggleVerseSelection(verse: IBibleVerseModel): void {
        const verses = this.bibleVerses();
        const selectedIds = this.selectedVerseIds();
        const alreadySelected = selectedIds.includes(verse.id);

        if (alreadySelected) {
            // Deselect all if any selected one is clicked again
            this.selectedVerseIds.set([]);
            return;
        }

        if (selectedIds.length === 0) {
            // First verse selected
            this.selectedVerseIds.set([verse.id]);
        } else {
            // Allow selecting up to 3 consecutive verses
            const currentIndices = selectedIds.map((id) => verses.findIndex((v) => v.id === id)).sort((a, b) => a - b);
            const newIndex = verses.findIndex((v) => v.id === verse.id);

            const firstIndex = currentIndices[0];
            const lastIndex = currentIndices[currentIndices.length - 1];

            const canSelectNext = (newIndex === lastIndex + 1 || newIndex === firstIndex - 1) && selectedIds.length < 3;

            if (canSelectNext) {
                this.selectedVerseIds.update((prev) =>
                    [...prev, verse.id].sort(
                        (a, b) => verses.findIndex((v) => v.id === a) - verses.findIndex((v) => v.id === b),
                    ),
                );
            }
        }
    }

    protected loadBibleVerses(chapterId: number): void {
        this.selectedChapterId.set(chapterId);
        this.loaderService.show();
        this.bibleService
            .getBibleVerses(chapterId)
            .pipe(
                tap((response) => {
                    this.bibleVerses.set(response);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error loading bible verses:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    // When user clicks Add
    protected onAddSelectedVerses(): void {
        // If user selected a verse from search
        const selectedId = this.selectedSearchVerseId();
        if (selectedId) {
            const verse = this.showSearchVersesResult().find((v) => v.id === selectedId);
            if (verse) {
                this.dialogRef.close([verse]);
                return;
            }
        }

        //  Otherwise, fallback to normal multiple selection
        const verses = this.selectedVerses();
        if (verses.length === 0) {
            return;
        }

        this.dialogRef.close(verses);
    }

    protected toggleSearchVerseSelection(verse: IBibleVerseModel): void {
        const selectedIds = this.selectedVerseIds();
        if (selectedIds.includes(verse.id)) {
            this.selectedVerseIds.set([]);
        } else {
            this.selectedVerseIds.set([verse.id]);
        }
    }

    // Called on (input)
    protected onSearchInput(query: string): void {
        this.searchSubject.next(query);
    }

    // Debounced search logic
    private setupSearchListener(): void {
        this.searchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                switchMap((query) => {
                    if (!query) {
                        this.showSearchVersesResult.set([]);
                        this.selectedSearchVerseId.set(null);
                        return of([]);
                    }

                    this.loaderService.show();
                    return this.bibleService.searchBibleVerses(query).pipe(
                        catchError((error) => {
                            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                            errorPopupConfig.data = {
                                title: `Error`,
                                message: error.error?.message,
                            };
                            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                            console.error('Search error:', error);
                            return of([]);
                        }),
                        finalize(() => {
                            this.loaderService.hide();
                        }),
                    );
                }),
            )
            .subscribe((results) => {
                this.showSearchVersesResult.set(results);
                document.getElementById('bibleSearchInput')?.focus();
            });
    }

    private loadBibleBooks(): void {
        this.loaderService.show();
        this.bibleService
            .getBibleTypeAndChapters()
            .pipe(
                tap((response) => {
                    this.bibleBooks.set(response);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error loading bible books & chapters:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }
}
